import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import html2canvas from "html2canvas";
import {
  answerAdminReportLearningQuestion,
  getAdminReportLearningFeedback,
  retryAdminReportLearningFeedback,
  submitAdminReportLearningFeedback,
  uploadAdminReportLearningCapture,
} from "../../services/api";

const st = {
  panel: { border: "1px solid var(--teal-border)", borderRadius: 8, padding: 14, background: "color-mix(in srgb, var(--teal) 5%, var(--bg-card))", marginTop: 12 },
  row: { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" },
  title: { fontSize: 15, fontWeight: 900, color: "var(--text-primary)" },
  hint: { fontSize: 12, color: "var(--text-muted)", lineHeight: 1.5 },
  textarea: { width: "100%", minHeight: 120, resize: "vertical", boxSizing: "border-box", marginTop: 8, padding: 10, border: "1.5px solid var(--border)", borderRadius: 6, background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", font: "inherit" },
  button: { border: "none", borderRadius: 6, padding: "8px 12px", background: "var(--teal)", color: "#fff", fontWeight: 800, cursor: "pointer" },
  secondary: { border: "1px solid var(--border)", borderRadius: 6, padding: "8px 12px", background: "var(--dashboard-selector-bg)", color: "var(--text-primary)", fontWeight: 800, cursor: "pointer" },
  danger: { border: "1px solid #fecaca", borderRadius: 6, padding: "8px 12px", background: "#fef2f2", color: "#b91c1c", fontWeight: 800, cursor: "pointer" },
  card: { border: "1px solid var(--border)", borderRadius: 8, padding: 10, background: "var(--bg-card)", marginTop: 10 },
  comparisonCard: { border: "1px solid var(--border)", borderRadius: 8, padding: 12, background: "var(--bg-card)", marginTop: 10 },
  comparisonGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%,220px),1fr))", gap: 10, marginTop: 10 },
  comparisonSide: { border: "1px solid var(--border)", borderRadius: 7, padding: 10, minWidth: 0 },
  comparisonLabel: { fontSize: 11, fontWeight: 900, letterSpacing: ".06em", textTransform: "uppercase", color: "var(--text-muted)" },
  comparisonRow: { display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", paddingTop: 7, marginTop: 7, borderTop: "1px solid var(--border-subtle)" },
  historyItem: { borderLeft: "3px solid var(--teal-border)", padding: "2px 0 2px 12px", marginTop: 12 },
  privateFeedback: { whiteSpace: "pre-wrap", lineHeight: 1.55, padding: 10, marginTop: 7, borderRadius: 7, background: "var(--dashboard-selector-bg)", border: "1px solid var(--border)", color: "var(--text-primary)", fontSize: 13 },
  unsentDraft: { border: "2px solid #dc2626", borderRadius: 8, padding: 12, marginTop: 12, background: "color-mix(in srgb, #dc2626 4%, var(--bg-card))", boxShadow: "0 0 0 2px rgba(220,38,38,.1)" },
  badge: { display: "inline-flex", borderRadius: 999, padding: "4px 8px", background: "var(--teal-bg)", color: "var(--teal-dark)", fontSize: 11, fontWeight: 900 },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 10, marginTop: 10 },
  image: { width: "100%", height: 120, objectFit: "cover", borderRadius: 6, border: "1px solid var(--border)" },
  overlay: { position: "fixed", inset: 0, zIndex: 2200, background: "rgba(15,23,42,.2)", cursor: "crosshair", touchAction: "none" },
  selection: { position: "fixed", border: "2px solid #14b8a6", background: "rgba(20,184,166,.12)", pointerEvents: "none" },
  captureBoundary: { position: "fixed", border: "2px dashed rgba(20,184,166,.9)", background: "transparent", pointerEvents: "none" },
  dockedPanel: { borderTop: "1px solid var(--teal-border)", paddingTop: 10, marginTop: 10 },
  unavailable: { display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", borderTop: "1px solid var(--border)", marginTop: 10, paddingTop: 10 },
};

function sample(detail) { return detail?.report || detail?.sample || {}; }
function sensitiveValues(detail, sampleId) {
  const source = sample(detail);
  return [sampleId, source.customer_name, source.customer_email, source.vin, source.vehicle_vin,
    source.ac_customer_id, source.customer_id, source.customer_phone_number]
    .map((value) => String(value || "").trim()).filter((value) => value.length >= 3);
}
function maskClone(clonedDocument, values) {
  const walker = clonedDocument.createTreeWalker(clonedDocument.body, clonedDocument.defaultView.NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    let text = node.nodeValue || "";
    values.forEach((value) => { text = text.replace(new RegExp(value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), "[MASKED]"); });
    node.nodeValue = text;
  });
  clonedDocument.querySelectorAll("input,textarea").forEach((element) => {
    let value = element.value || "";
    values.forEach((identity) => { value = value.replace(new RegExp(identity.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"), "gi"), "[MASKED]"); });
    element.value = value;
  });
}
function rootElement() {
  const root = document.querySelector("[data-report-learning-capture-root='true']");
  if (!root) throw new Error("The report content is not ready to capture. Reopen the report and try again.");
  return root;
}
function hasUnsupportedColor(value) {
  return /\bcolor(?:-mix)?\(/i.test(value || "");
}
function createColorResolver() {
  const canvas = document.createElement("canvas");
  canvas.width = 1; canvas.height = 1;
  const context = canvas.getContext("2d", { willReadFrequently: true });
  const cache = new Map();
  return (value) => {
    if (!value || !context || !hasUnsupportedColor(value)) return value;
    if (cache.has(value)) return cache.get(value);
    context.clearRect(0, 0, 1, 1);
    context.fillStyle = "rgba(0,0,0,0)";
    context.fillStyle = value;
    context.fillRect(0, 0, 1, 1);
    const [red, green, blue, alpha] = context.getImageData(0, 0, 1, 1).data;
    const resolved = `rgba(${red}, ${green}, ${blue}, ${(alpha / 255).toFixed(3)})`;
    cache.set(value, resolved);
    return resolved;
  };
}
function normalizeCloneColors(originalRoot, clonedRoot) {
  if (!originalRoot || !clonedRoot) return;
  const originals = [originalRoot, ...originalRoot.querySelectorAll("*")];
  const clones = [clonedRoot, ...clonedRoot.querySelectorAll("*")];
  const resolveColor = createColorResolver();
  const colorProperties = ["color", "backgroundColor", "borderTopColor", "borderRightColor", "borderBottomColor", "borderLeftColor", "outlineColor", "textDecorationColor", "columnRuleColor"];
  const count = Math.min(originals.length, clones.length);
  for (let index = 0; index < count; index += 1) {
    const computed = getComputedStyle(originals[index]);
    const cloneStyle = clones[index].style;
    colorProperties.forEach((property) => {
      const value = computed[property];
      if (hasUnsupportedColor(value)) cloneStyle[property] = resolveColor(value);
    });
    if (hasUnsupportedColor(computed.backgroundImage)) cloneStyle.backgroundImage = "none";
    if (hasUnsupportedColor(computed.boxShadow)) cloneStyle.boxShadow = "none";
    if (hasUnsupportedColor(computed.textShadow)) cloneStyle.textShadow = "none";
    if (hasUnsupportedColor(computed.filter)) cloneStyle.filter = "none";
  }
}
async function renderReport(detail, sampleId) {
  const root = rootElement();
  const values = sensitiveValues(detail, sampleId);
  const pageBackground = getComputedStyle(document.documentElement).getPropertyValue("--bg-page").trim() || "#fff";
  return html2canvas(root, {
    backgroundColor: hasUnsupportedColor(pageBackground) ? "#fff" : pageBackground,
    scale: Math.min(window.devicePixelRatio || 1, 2), useCORS: true, logging: false,
    ignoreElements: (element) => Boolean(element.closest?.("[data-learning-capture-exclude='true']")),
    onclone: (doc, clonedRoot) => { maskClone(doc, values); normalizeCloneColors(root, clonedRoot); },
  });
}
async function renderVisibleReport(detail, sampleId) {
  const root = rootElement(); const bounds = root.getBoundingClientRect(); const full = await renderReport(detail, sampleId);
  const left = Math.max(bounds.left, 0); const top = Math.max(bounds.top, 0);
  const right = Math.min(bounds.right, window.innerWidth); const bottom = Math.min(bounds.bottom, window.innerHeight);
  if (right - left < 20 || bottom - top < 20) throw new Error("No report content is visible to capture.");
  const x = left - bounds.left; const y = top - bounds.top;
  const crop = document.createElement("canvas");
  crop.width = Math.max(1, Math.round((right - left) / bounds.width * full.width));
  crop.height = Math.max(1, Math.round((bottom - top) / bounds.height * full.height));
  crop.getContext("2d").drawImage(full, Math.round(x / bounds.width * full.width), Math.round(y / bounds.height * full.height), crop.width, crop.height, 0, 0, crop.width, crop.height);
  return crop;
}
function resizeCanvas(source, max = 2048) {
  const scale = Math.min(1, max / Math.max(source.width, source.height));
  if (scale === 1) return source;
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(source.width * scale)); canvas.height = Math.max(1, Math.round(source.height * scale));
  canvas.getContext("2d").drawImage(source, 0, 0, canvas.width, canvas.height); return canvas;
}
function canvasFile(canvas, name) {
  return new Promise((resolve, reject) => canvas.toBlob((blob) => {
    if (!blob) { reject(new Error("Unable to create the capture.")); return; }
    resolve(new File([blob], name, { type: "image/png" }));
  }, "image/png"));
}
function changedMetrics(before = {}, after = {}) {
  const keys = new Set([...Object.keys(before), ...Object.keys(after)]);
  return [...keys].filter((key) => JSON.stringify(before[key]) !== JSON.stringify(after[key]));
}

function technicalPreviewThresholds(currentThresholds, proposal, changed) {
  if (!proposal?.after?.thresholds) return null;
  if (proposal.before?.templateName !== proposal.after?.templateName || !currentThresholds) return proposal.after.thresholds;
  const merged = { ...currentThresholds };
  changed.forEach((key) => { merged[key] = proposal.after.thresholds[key]; });
  return merged;
}

function thresholdTypeLabel(type) {
  if (type === "GT") return "Higher values increase concern";
  if (type === "BOOL") return "Detected / not detected";
  if (type === "VISCOSITY_100" || type === "VISCOSITY_40") return "Viscosity grade band";
  return type || "Standard threshold";
}

function thresholdComparisonRows(before = {}, after = {}) {
  const type = after.type || before.type;
  const rows = type === "GT"
    ? [["warn", "Caution begins"], ["fail", "Critical begins"]]
    : (type === "VISCOSITY_100" || type === "VISCOSITY_40")
      ? [["warn", "Normal lower boundary"], ["pass", "Normal upper boundary"], ["fail", "Configured critical value"]]
      : [["pass", "Pass value"], ["warn", "Caution value"], ["fail", "Critical value"]];
  const meaningful = rows.filter(([field]) => before[field] !== undefined || after[field] !== undefined)
    .filter(([field]) => before[field] !== 0 || after[field] !== 0 || before[field] !== after[field]);
  if ((before.type || "") !== (after.type || "")) meaningful.unshift(["type", "Evaluation"]);
  return meaningful;
}

function thresholdValue(threshold, field) {
  if (field === "type") return thresholdTypeLabel(threshold?.type);
  const value = threshold?.[field];
  if (value === null || value === undefined || value === "") return "Not set";
  return `${value}${threshold?.unit ? ` ${threshold.unit}` : ""}`;
}

function ThresholdComparison({ metricKey, before = {}, after = {} }) {
  const rows = thresholdComparisonRows(before, after);
  const label = after.label || before.label || metricKey.replaceAll("_", " ");
  const renderSide = (title, threshold, proposed = false) => <div style={{ ...st.comparisonSide, ...(proposed ? { borderColor: "var(--teal-border)", background: "var(--teal-bg)" } : {}) }}>
    <div style={{ ...st.comparisonLabel, ...(proposed ? { color: "var(--teal-dark)" } : {}) }}>{title}</div>
    {rows.map(([field, rowLabel]) => <div key={field} style={st.comparisonRow}><span style={st.hint}>{rowLabel}</span><strong style={{ whiteSpace: "nowrap", color: "var(--text-primary)" }}>{thresholdValue(threshold, field)}</strong></div>)}
  </div>;
  return <section style={st.comparisonCard} aria-label={`${label} threshold comparison`}>
    <div style={st.title}>{label}</div>
    <div style={st.comparisonGrid}>
      {renderSide("Current", before)}
      {renderSide("Learning Lab proposal", after, true)}
    </div>
  </section>;
}

function ordinal(value) {
  const number = Number(value);
  const suffix = number % 100 >= 11 && number % 100 <= 13 ? "th" : number % 10 === 1 ? "st" : number % 10 === 2 ? "nd" : number % 10 === 3 ? "rd" : "th";
  return `${number}${suffix}`;
}

function clarificationAnswer(answer) {
  if (typeof answer === "string") return answer;
  return answer?.text || "";
}

function FeedbackHistory({ cycles = [] }) {
  const ordered = [...cycles].sort((a, b) => Number(a.cycle) - Number(b.cycle));
  return <section style={{ ...st.comparisonCard, background: "color-mix(in srgb, var(--teal) 4%, var(--bg-card))" }} aria-label="Lake feedback history">
    <div style={st.title}>Lake's Feedback History</div>
    <div style={st.hint}>These private instructions and clarification answers produced the current Learning Lab review.</div>
    {ordered.map((cycle) => <article key={cycle.id} style={st.historyItem}>
      <div style={st.row}>
        <strong>Cycle {cycle.cycle} feedback</strong>
        <span style={st.badge}>Generated {ordinal(Number(cycle.cycle) + 1)} review</span>
        <span style={st.hint}>{cycle.createdAt ? new Date(cycle.createdAt).toLocaleString() : ""}</span>
      </div>
      <div style={{ ...st.comparisonLabel, marginTop: 9 }}>Lake entered</div>
      <div style={st.privateFeedback}>{cycle.instructions || "No written instructions recorded."}</div>
      {cycle.questions?.filter((question) => clarificationAnswer(question.answer)).map((question) => <div key={question.id} style={{ marginTop: 10 }}>
        <div style={st.hint}><strong>Learning Lab clarification:</strong> {question.detail}</div>
        <div style={st.privateFeedback}><strong>Lake answered:</strong> {clarificationAnswer(question.answer)}</div>
      </div>)}
      {cycle.attachments?.length > 0 && <div style={st.grid}>{cycle.attachments.map((attachment) => <figure key={attachment.id} style={{ margin: 0 }}><img alt={attachment.caption || `Cycle ${cycle.cycle} masked report capture`} src={attachment.thumbnailUrl} style={st.image} /><figcaption style={st.hint}>{attachment.caption || attachment.captureType.replaceAll("_", " ")}</figcaption></figure>)}</div>}
    </article>)}
  </section>;
}

const ReportLearningFeedbackPanel = forwardRef(function ReportLearningFeedbackPanel({ sampleId, detail, disabled, docked = false, integratedComparison = false, onSubmitted, onPreviewChange, onApprovalContextChange, draftMemory, onDraftStateChange }, ref) {
  const localDraftMemoryRef = useRef({ drafts: new Map(), urls: new Set() });
  const memory = draftMemory || localDraftMemoryRef.current;
  const initialDraft = memory.drafts.get(sampleId);
  const [history, setHistory] = useState(null);
  const [instructions, setInstructions] = useState(initialDraft?.instructions || "");
  const [captures, setCaptures] = useState(initialDraft?.captures || []);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  const [selection, setSelection] = useState(null);
  const [futureUseDecision, setFutureUseDecision] = useState("");
  const [noteFinalizationReady, setNoteFinalizationReady] = useState(false);
  const [clarificationAnswers, setClarificationAnswers] = useState({});
  const [expanded, setExpanded] = useState(false);
  const activeSampleIdRef = useRef(sampleId);
  const instructionsRef = useRef(initialDraft?.instructions || "");
  const capturesRef = useRef(initialDraft?.captures || []);
  const loadRequestRef = useRef(0);
  const selectionStartRef = useRef(null);

  const latest = history?.latest || null;
  const ready = latest?.status === "ready_for_review";
  const proposal = ready ? latest.stagedResult : null;
  const hasUnsentFeedback = instructions.length > 0 || captures.length > 0;
  const changed = useMemo(() => changedMetrics(proposal?.before?.thresholds, proposal?.after?.thresholds), [proposal]);
  const previewThresholds = useMemo(() => technicalPreviewThresholds(detail?.thresholds, proposal, changed), [detail?.thresholds, proposal, changed]);

  function persistDraft(reportId, nextInstructions, nextCaptures) {
    const hasDraft = nextInstructions.length > 0 || nextCaptures.length > 0;
    if (hasDraft) memory.drafts.set(reportId, { instructions: nextInstructions, captures: nextCaptures, updatedAt: Date.now() });
    else memory.drafts.delete(reportId);
    onDraftStateChange?.(reportId, hasDraft);
  }

  function updateInstructions(value) {
    instructionsRef.current = value;
    setInstructions(value);
    persistDraft(activeSampleIdRef.current, value, capturesRef.current);
  }

  function updateCaptures(updater) {
    const next = typeof updater === "function" ? updater(capturesRef.current) : updater;
    capturesRef.current = next;
    setCaptures(next);
    persistDraft(activeSampleIdRef.current, instructionsRef.current, next);
  }

  function clearUnsentFeedback(reportId = activeSampleIdRef.current) {
    const draftCaptures = reportId === activeSampleIdRef.current
      ? capturesRef.current
      : memory.drafts.get(reportId)?.captures || [];
    draftCaptures.forEach((capture) => {
      if (!capture.previewUrl) return;
      URL.revokeObjectURL(capture.previewUrl);
      memory.urls.delete(capture.previewUrl);
    });
    memory.drafts.delete(reportId);
    onDraftStateChange?.(reportId, false);
    if (reportId === activeSampleIdRef.current) {
      instructionsRef.current = "";
      capturesRef.current = [];
      setInstructions("");
      setCaptures([]);
    }
  }

  useImperativeHandle(ref, () => ({
    hasUnsentFeedback,
    submitUnsentFeedback: send,
    discardUnsentFeedback: () => { clearUnsentFeedback(); setMessage(null); },
  }));

  async function load(requestedSampleId = sampleId) {
    const requestId = ++loadRequestRef.current;
    try {
      const res = await getAdminReportLearningFeedback(requestedSampleId);
      if (requestId === loadRequestRef.current && activeSampleIdRef.current === requestedSampleId) setHistory(res.data);
    } catch (err) {
      if (requestId !== loadRequestRef.current || activeSampleIdRef.current !== requestedSampleId) return;
      if (err.response?.data?.code === "REPORT_FEEDBACK_DISABLED") setHistory({ featureEnabled: false });
      else setMessage({ type: "err", text: err.response?.data?.error || "Unable to load Learning Lab feedback." });
    }
  }
  useEffect(() => {
    const previousSampleId = activeSampleIdRef.current;
    if (previousSampleId !== sampleId) {
      persistDraft(previousSampleId, instructionsRef.current, capturesRef.current);
      const restored = memory.drafts.get(sampleId);
      activeSampleIdRef.current = sampleId;
      instructionsRef.current = restored?.instructions || "";
      capturesRef.current = restored?.captures || [];
      setInstructions(instructionsRef.current);
      setCaptures(capturesRef.current);
      setHistory(null);
      setSelection(null);
      selectionStartRef.current = null;
      setClarificationAnswers({});
      setNoteFinalizationReady(false);
      setFutureUseDecision("");
      setMessage(restored ? { type: "ok", text: `Unsent feedback restored for ${sampleId}.` } : null);
      onPreviewChange?.(null);
      onApprovalContextChange?.(null);
    }
    load(sampleId);
    return () => { loadRequestRef.current += 1; };
  }, [sampleId]);
  useEffect(() => { setNoteFinalizationReady(false); setFutureUseDecision(""); }, [latest?.id]);
  useEffect(() => () => {
    if (draftMemory) return;
    memory.urls.forEach((url) => URL.revokeObjectURL(url));
    memory.urls.clear();
    memory.drafts.clear();
  }, [draftMemory, memory]);
  useEffect(() => {
    onPreviewChange?.(!integratedComparison && proposal?.after ? {
      thresholds: previewThresholds,
      templateName: proposal.after.templateName,
      oilGeekNote: noteFinalizationReady ? proposal.after.note : proposal.before?.note,
      stagedLearningPreview: true,
    } : null);
    onApprovalContextChange?.(!integratedComparison && ready ? {
      required: true,
      noteFinalized: noteFinalizationReady,
      ...(noteFinalizationReady && futureUseDecision ? {
        submissionId: latest.id,
        proposalHash: latest.proposalHash,
        futureUseDecision,
      } : {}),
    } : null);
  }, [ready, futureUseDecision, integratedComparison, noteFinalizationReady, latest?.id, latest?.proposalHash, proposal, previewThresholds, onPreviewChange, onApprovalContextChange]);
  useEffect(() => { if (latest && (ACTIVE(latest.status) || ready)) setExpanded(true); }, [latest, ready]);
  useEffect(() => {
    if (!hasUnsentFeedback) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [hasUnsentFeedback]);
  useEffect(() => {
    if (!selection) return undefined;
    const cancelSelection = (event) => {
      if (event.key !== "Escape") return;
      setSelection(null); selectionStartRef.current = null; setExpanded(true);
    };
    window.addEventListener("keydown", cancelSelection);
    return () => window.removeEventListener("keydown", cancelSelection);
  }, [selection]);

  async function addCapture(canvas, captureType, cropCoordinates = {}) {
    const resized = resizeCanvas(canvas);
    const file = await canvasFile(resized, `${sampleId}-${captureType}-${Date.now()}.png`);
    if (file.size > 5 * 1024 * 1024) throw new Error("The capture is larger than 5 MB after resizing.");
    const previewUrl = URL.createObjectURL(file); memory.urls.add(previewUrl);
    updateCaptures((current) => [...current, { id: `${Date.now()}-${Math.random()}`, file, captureType,
      cropCoordinates, width: resized.width, height: resized.height, previewUrl, caption: "" }]);
  }
  async function captureCurrent() {
    if (captures.length >= 5) { setMessage({ type: "err", text: "A feedback cycle can include up to five captures." }); return; }
    setBusy(true); setMessage({ type: "ok", text: "Capturing the visible report and masking identity fields…" });
    try { await addCapture(await renderVisibleReport(detail, sampleId), "current_view"); setMessage({ type: "ok", text: "Visible report view captured and masked." }); }
    catch (err) { setMessage({ type: "err", text: err.message || "Unable to capture the report." }); }
    finally { setBusy(false); }
  }
  function beginPin() {
    if (captures.length >= 5) { setMessage({ type: "err", text: "A feedback cycle can include up to five captures." }); return; }
    try {
      rootElement();
      setExpanded(false); setSelection({ left: 0, top: 0, width: 0, height: 0 }); selectionStartRef.current = null;
    } catch (err) { setMessage({ type: "err", text: err.message }); }
  }
  function cancelPin() {
    setSelection(null); selectionStartRef.current = null; setExpanded(true);
  }
  function startPin(event) {
    event.currentTarget.setPointerCapture?.(event.pointerId);
    selectionStartRef.current = { x: event.clientX, y: event.clientY };
    setSelection({ left: event.clientX, top: event.clientY, width: 0, height: 0 });
  }
  function movePin(event) {
    const start = selectionStartRef.current;
    if (!start) return;
    setSelection({ left: Math.min(start.x, event.clientX), top: Math.min(start.y, event.clientY), width: Math.abs(event.clientX - start.x), height: Math.abs(event.clientY - start.y) });
  }
  async function finishPin(event) {
    const selectionStart = selectionStartRef.current;
    if (!selectionStart) { setSelection(null); return; }
    event.currentTarget.releasePointerCapture?.(event.pointerId);
    const left = Math.min(selectionStart.x, event.clientX); const top = Math.min(selectionStart.y, event.clientY);
    const width = Math.abs(event.clientX - selectionStart.x); const height = Math.abs(event.clientY - selectionStart.y);
    setSelection(null); selectionStartRef.current = null; setExpanded(true);
    if (width < 20 || height < 20) { setMessage({ type: "err", text: "Drag a larger area of the report." }); return; }
    setBusy(true); setMessage({ type: "ok", text: "Capturing and masking the selected area…" });
    try {
      const root = rootElement(); const bounds = root.getBoundingClientRect(); const full = await renderReport(detail, sampleId);
      const right = Math.min(left + width, bounds.right); const bottom = Math.min(top + height, bounds.bottom);
      const clippedLeft = Math.max(left, bounds.left); const clippedTop = Math.max(top, bounds.top);
      const clippedWidth = right - clippedLeft; const clippedHeight = bottom - clippedTop;
      if (clippedWidth < 20 || clippedHeight < 20) throw new Error("Select an area inside the report.");
      const x = clippedLeft - bounds.left; const y = clippedTop - bounds.top;
      const crop = document.createElement("canvas");
      crop.width = Math.max(1, Math.round(clippedWidth / bounds.width * full.width)); crop.height = Math.max(1, Math.round(clippedHeight / bounds.height * full.height));
      crop.getContext("2d").drawImage(full, Math.round(x / bounds.width * full.width), Math.round(y / bounds.height * full.height), crop.width, crop.height, 0, 0, crop.width, crop.height);
      await addCapture(crop, "pinned_area", { x: x / bounds.width, y: y / bounds.height, width: clippedWidth / bounds.width, height: clippedHeight / bounds.height });
      setMessage({ type: "ok", text: "Pinned area captured and masked." });
    } catch (err) { setMessage({ type: "err", text: err.message || "Unable to capture the selected area." }); }
    finally { setBusy(false); }
  }
  async function send() {
    if (busy) return false;
    if (!instructions.trim()) { setMessage({ type: "err", text: "Enter written instructions for the Learning Lab." }); return; }
    setBusy(true); setMessage({ type: "ok", text: "Securing captures and sending feedback…" });
    try {
      const uploaded = [];
      for (const capture of captures) {
        const binding = await uploadAdminReportLearningCapture(sampleId, capture.file);
        uploaded.push({ objectBindingId: binding.id, captureType: capture.captureType, cropCoordinates: capture.cropCoordinates,
          caption: capture.caption, width: capture.width, height: capture.height,
          redactionManifest: { clientMasked: true, maskedFields: ["customerName", "customerEmail", "sampleId", "vin", "account", "phone"], version: "dom-mask-v1" } });
      }
      const res = await submitAdminReportLearningFeedback(sampleId, { instructions: instructions.trim(), attachments: uploaded,
        expectedReportSourceHash: history?.reportSourceHash, expectedReviewSourceHash: history?.reviewSourceHash });
      setMessage({ type: "ok", text: integratedComparison ? "Feedback is in the Learning Lab. You can continue reviewing this report while it processes." : "Feedback is in the Learning Lab. Returning to Reports to Review…" });
      clearUnsentFeedback();
      onSubmitted?.(res.data);
      return true;
    } catch (err) {
      if (err.response?.data?.code === "REPORT_FEEDBACK_DISABLED") {
        setHistory({ featureEnabled: false }); setMessage(null);
      } else setMessage({ type: "err", text: err.response?.data?.error || err.message || "Unable to send feedback." });
      return false;
    }
    finally { setBusy(false); }
  }
  async function retry() {
    setBusy(true); try { await retryAdminReportLearningFeedback(sampleId, latest.id); await load(); }
    catch (err) { setMessage({ type: "err", text: err.response?.data?.error || "Unable to retry processing." }); } finally { setBusy(false); }
  }
  async function answer(question) {
    const answerText = clarificationAnswers[question.id]?.trim(); if (!answerText) return;
    setBusy(true); try { await answerAdminReportLearningQuestion(sampleId, latest.id, question.id, answerText); await load(); }
    catch (err) { setMessage({ type: "err", text: err.response?.data?.error || "Unable to answer the clarification question." }); } finally { setBusy(false); }
  }

  function prepareFinalNote() {
    if (!futureUseDecision) {
      setMessage({ type: "err", text: "Choose whether the confirmed correction is for this report only or matching future reports." });
      return;
    }
    setNoteFinalizationReady(true);
    setMessage({ type: "ok", text: "Technical corrections accepted for final review. Review the Proposed Oil Geek Note before approving." });
  }

  function returnToTechnicalReview() {
    setNoteFinalizationReady(false);
    setMessage({ type: "ok", text: "Returned to technical feedback review. The customer-facing note has not been changed." });
  }

  if (!history) return null;
  if (history.featureEnabled === false) return <div style={st.unavailable} data-learning-capture-exclude="true" role="status">
    <strong>Learning Lab Feedback</strong><span style={st.badge}>Temporarily unavailable</span><span style={st.hint}>Processing is paused by the administrator. No feedback was submitted.</span>
  </div>;
  return <div style={docked ? st.dockedPanel : st.panel} data-learning-capture-exclude="true">
    <div style={{ ...st.row, justifyContent: "space-between" }}><div><div style={st.title}>Learning Lab Feedback</div><div style={st.hint}>Private instructions and captures are never shown to the customer. Processing cannot approve, release, or publish changes.</div></div><div style={st.row}>{hasUnsentFeedback && <span style={{ ...st.badge, background: "#fef2f2", color: "#b91c1c", border: "1px solid #fecaca" }}>Unsaved Learning Lab Feedback</span>}{latest && <span style={st.badge}>Cycle {latest.cycle} · {latest.status.replaceAll("_", " ")}</span>}<button type="button" style={st.secondary} aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>{expanded ? "Hide Feedback" : "Open Feedback"}</button></div></div>
    {expanded && <div className={docked ? "sdx-admin-review-feedback-scroll" : undefined}>

    {latest?.status === "needs_clarification" && <div style={st.card}><strong>Needs clarification</strong>{latest.questions?.filter((q) => q.status === "open").map((question) => <div key={question.id} style={{ marginTop: 10 }}><div style={st.hint}>{question.detail}</div><textarea style={{ ...st.textarea, minHeight: 70 }} value={clarificationAnswers[question.id] || ""} onChange={(event) => setClarificationAnswers((current) => ({ ...current, [question.id]: event.target.value }))} /><button type="button" style={st.button} disabled={busy} onClick={() => answer(question)}>Answer and Resume</button></div>)}</div>}
    {latest?.status === "failed" && <div style={st.card}><strong>Processing failed</strong><div style={st.hint}>{latest.failureMessage || "The Learning Lab job did not complete."}</div><button type="button" style={st.button} disabled={busy} onClick={retry}>Retry</button></div>}
    {ready && <FeedbackHistory cycles={history?.cycles || []} />}
    {ready && <div style={st.card}>
      <div style={st.title}>Ready for {latest.cycle + 1}{latest.cycle + 1 === 2 ? "nd" : latest.cycle + 1 === 3 ? "rd" : "th"} Review</div>
      <div style={{ ...st.comparisonLabel, marginTop: 10 }}>Learning Lab response</div>
      <p style={st.hint}>{proposal.interpretation?.summary}</p>
      <div style={st.grid}>
        <div><strong>Template</strong><div style={st.hint}>{proposal.before?.templateName} → {proposal.after?.templateName}</div></div>
        <div><strong>Technical scope</strong><div style={st.hint}>{changed.length ? `${changed.length} explicitly proposed metric change${changed.length === 1 ? "" : "s"}` : "No threshold changes proposed"}</div></div>
      </div>
      {changed.map((key) => <ThresholdComparison key={key} metricKey={key} before={proposal.before?.thresholds?.[key]} after={proposal.after?.thresholds?.[key]} />)}
      {proposal.interpretation?.futureRule?.proposed && <div style={{ ...st.hint, marginTop: 10 }}><strong>Candidate future rule:</strong> {proposal.interpretation.futureRule.summary} · Scope: {proposal.interpretation.futureRule.scope}</div>}
      {proposal.interpretation?.immutableDataDiscrepancies?.length > 0 && <div style={{ ...st.hint, marginTop: 10 }}><strong>Immutable data discrepancies:</strong> {proposal.interpretation.immutableDataDiscrepancies.join("; ")}</div>}
      {proposal.interpretation?.uiIssues?.length > 0 && <div style={{ ...st.hint, marginTop: 10 }}><strong>Developer attention:</strong> {proposal.interpretation.uiIssues.join("; ")}</div>}
      {proposal.interpretation?.citations?.length > 0 && <div style={{ ...st.hint, marginTop: 10 }}>Evidence: {proposal.interpretation.citations.join(", ")}</div>}
      <div style={{ ...st.hint, marginTop: 10 }}>{integratedComparison ? "Use Report Threshold Comparison to preview and select these changes. Future learning is separate and does not block release." : "Only explicitly proposed technical changes are overlaid on the report's current configuration. Unrelated thresholds and the customer-facing Oil Geek Note remain unchanged during feedback review."}</div>
      {!integratedComparison && <>
      <fieldset style={{ border: "none", padding: 0, marginTop: 12 }}>
        <legend style={{ fontWeight: 900 }}>How should the confirmed learning be used?</legend>
        <label style={{ display: "block", marginTop: 7 }}><input type="radio" name={`future-use-${latest.id}`} checked={futureUseDecision === "matching_future_reports"} onChange={() => setFutureUseDecision("matching_future_reports")} /> Use for matching future reports (still requires guarded publication)</label>
        <label style={{ display: "block", marginTop: 7 }}><input type="radio" name={`future-use-${latest.id}`} checked={futureUseDecision === "this_report_only"} onChange={() => setFutureUseDecision("this_report_only")} /> This report only</label>
      </fieldset>
      {!noteFinalizationReady
        ? <button type="button" style={{ ...st.button, marginTop: 12 }} onClick={prepareFinalNote}>Accept Corrections and Prepare Final Note</button>
        : <div style={{ ...st.row, marginTop: 12 }}><span style={st.badge}>Technical corrections accepted for final review</span><button type="button" style={st.secondary} onClick={returnToTechnicalReview}>Return to Technical Review</button></div>}
      <div style={{ ...st.hint, marginTop: 8 }}>Nothing is saved or released until Lake uses an approval action.</div>
      </>}
    </div>}

    {!ready && history?.cycles?.length > 0 && <details style={st.card}><summary style={{ cursor: "pointer", fontWeight: 900 }}>Feedback and capture history ({history.cycles.length})</summary>{history.cycles.map((cycle) => <div key={cycle.id} style={{ marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }}><div style={st.row}><strong>Cycle {cycle.cycle}</strong><span style={st.badge}>{cycle.status.replaceAll("_", " ")}</span><span style={st.hint}>{cycle.createdAt ? new Date(cycle.createdAt).toLocaleString() : ""}</span></div><div style={{ ...st.hint, whiteSpace: "pre-wrap", marginTop: 6 }}>{cycle.instructions}</div>{cycle.attachments?.length > 0 && <div style={st.grid}>{cycle.attachments.map((attachment) => <figure key={attachment.id} style={{ margin: 0 }}><img alt={attachment.caption || `Cycle ${cycle.cycle} masked report capture`} src={attachment.thumbnailUrl} style={st.image} /><figcaption style={st.hint}>{attachment.caption || attachment.captureType.replaceAll("_", " ")}</figcaption></figure>)}</div>}</div>)}</details>}

    {!ACTIVE(latest?.status) && <div style={hasUnsentFeedback ? st.unsentDraft : { marginTop: 12 }} data-unsent-learning-feedback={hasUnsentFeedback ? "true" : "false"}>
      {ready && <div style={st.title}>Submit More Feedback</div>}
      {hasUnsentFeedback && <div role="status" style={{ color: "#b91c1c", fontWeight: 900, fontSize: 12 }}>Unsent feedback for {sampleId} is saved in this browser session.</div>}
      <label style={{ display: "block", marginTop: 12, fontWeight: 900 }}>Private instructions (required)<textarea style={{ ...st.textarea, ...(hasUnsentFeedback ? { borderColor: "#dc2626" } : {}) }} value={instructions} onChange={(event) => updateInstructions(event.target.value)} placeholder="Describe what is wrong, what should change, and whether the direction is report-specific or broader." maxLength={12000} /></label>
      <div style={st.row}><button type="button" style={st.secondary} disabled={busy || captures.length >= 5} onClick={captureCurrent}>Capture Current Report View</button><button type="button" style={st.secondary} disabled={busy || captures.length >= 5} onClick={beginPin}>Pin/Capture an Area</button><span style={st.hint}>{captures.length}/5 captures</span></div>
      {captures.length > 0 && <div style={st.grid}>{captures.map((capture) => <div key={capture.id} style={{ ...st.card, borderColor: "#dc2626" }}><img alt="Masked report capture preview" src={capture.previewUrl} style={st.image} /><label style={{ display: "block", marginTop: 6, fontSize: 12 }}>Optional capture caption<input style={{ width: "100%", boxSizing: "border-box", marginTop: 3 }} value={capture.caption} maxLength={500} onChange={(event) => updateCaptures((current) => current.map((item) => item.id === capture.id ? { ...item, caption: event.target.value } : item))} /></label><button type="button" style={{ ...st.danger, marginTop: 6 }} onClick={() => { URL.revokeObjectURL(capture.previewUrl); memory.urls.delete(capture.previewUrl); updateCaptures((current) => current.filter((item) => item.id !== capture.id)); }}>Remove</button></div>)}</div>}
      <button type="button" style={{ ...st.button, marginTop: 12 }} disabled={busy || disabled || !instructions.trim()} onClick={send}>{busy ? "Sending…" : integratedComparison ? "Send to Learning Lab" : "Send to Learning Lab and Return to Reports"}</button>
    </div>}
    {message && <div role={message.type === "err" ? "alert" : "status"} style={{ marginTop: 10, color: message.type === "err" ? "#dc2626" : "#166534", fontWeight: 800 }}>{message.text}</div>}
    </div>}
    {selection && createPortal(<div role="dialog" aria-label="Select a report area to capture" style={st.overlay} onPointerDown={startPin} onPointerMove={movePin} onPointerUp={finishPin} onPointerCancel={cancelPin}>{(() => { const bounds = rootElement().getBoundingClientRect(); return <div style={{ ...st.captureBoundary, left: Math.max(0, bounds.left), top: Math.max(0, bounds.top), width: Math.min(window.innerWidth, bounds.right) - Math.max(0, bounds.left), height: Math.min(window.innerHeight, bounds.bottom) - Math.max(0, bounds.top) }} />; })()}<div style={{ ...st.selection, ...selection }} /><div style={{ position: "fixed", top: 16, left: "50%", transform: "translateX(-50%)", background: "#0f172a", color: "#fff", padding: "8px 12px", borderRadius: 6, fontWeight: 800 }}>Drag inside the dashed report boundary to capture · Press Esc to cancel</div></div>, document.body)}
  </div>;
});

export default ReportLearningFeedbackPanel;

function ACTIVE(status) { return ["queued", "processing", "needs_clarification"].includes(status); }
